/**
 * Real, self-fetching Payroll Policies resolver (#22726).
 *
 * Replaces the old mock-backed, EDITABLE `usePayrollPolicies()` (which wired
 * `wagepro_store.tsx`'s `statutory_rates`/`update_statutory_rates` to
 * `MOCK_STATUTORY_RATES`, and `MOCK_TAX_BRACKETS` for the APIT brackets).
 * That was a design error, not a wiring gap: the real statutory rates are
 * LAW, hardcoded in the jurisdiction engine
 * (`packages/jumpstone-core/src/jurisdictions/lk/payroll/sl_payroll.ts` /
 * `sl_gratuity.ts`), and `payroll.approvePayRun` computes from that engine —
 * it never read the screen's mock values, so an admin "editing" EPF% there
 * changed nothing real. There is no write path here BY DESIGN.
 *
 * Mirrors `use_employees.ts`'s shape exactly (the closer analog — this is an
 * EMPLOYER-scoped `payroll:employer:read` procedure, not `use_my_pay.ts`'s
 * personalPayroll self-service path): `useOAuthEntityContext()` +
 * `getTrpcClient()` (never `firm.list`/`entity.list`), a DI-able
 * `fetchStatutoryPolicy` pure-ish async function + a pure
 * `resolvePayrollPoliciesState` decision function (both unit-testable
 * without React), and a thin `usePayrollPolicies()` hook wrapping them in
 * `useEffect`/`useState`.
 *
 * Failure classification follows `use_pay_runs.ts`'s more precise
 * `isPayrollAddonRequiredError` (not `use_employees.ts`'s broader
 * `isForbiddenTrpcError`) — this procedure is ALSO gated by
 * `requirePayrollAddon`, so a missing-add-on FORBIDDEN should resolve
 * `entitlement_required`, while any OTHER FORBIDDEN (a missing OAuth scope,
 * a null-audience token, no entity access) resolves the generic `error`
 * state instead of being misreported as "buy the add-on" — see that
 * function's own docblock in `lib/trpc.ts` for the full rationale.
 *
 * NO mock fallback — same stance as `use_my_pay.ts`/`use_employees.ts`: a
 * mock statutory policy shown to an admin as their own company's real
 * payroll law would be fabricated financial/legal data, not a harmless
 * placeholder. Every failure surfaces as an explicit `PayrollPoliciesState`
 * (`no_entity`/`entitlement_required`/`error`) instead.
 */
import * as React from 'react';
import { getTrpcClient, isPayrollAddonRequiredError } from '../lib/trpc.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';

/** One PAYE/APIT progressive band, as the wire returns it — `to_cents: null` means the open-ended top band. */
export interface StatutoryPayeBand {
  from_cents: number;
  to_cents: number | null;
  rate_bps: number;
  /** The IRD's printed "less Rs X" constant for the band (`rate x income - subtrahend`), in cents. */
  subtrahend_cents: number;
}

/**
 * The `payroll.getStatutoryPolicy` wire shape this hook consumes — a
 * discriminated union on `supported`, matching the tRPC procedure's
 * `.output()` schema exactly (`apps/api/src/trpc/routers/payroll.ts`).
 * `supported: false` means the entity's jurisdiction has no registered
 * statutory-policy content (WagePro is SL-only today, but `entities.country`
 * is a free column).
 */
export type StatutoryPolicyRecord =
  | {
      supported: true;
      country: string;
      epf_employee_rate_bps: number;
      epf_employer_rate_bps: number;
      etf_employer_rate_bps: number;
      paye_bands: {
        effective_from: string;
        /** The calendar date (YYYY-MM-DD) the table was resolved for. */
        as_of_date: string;
        bands: StatutoryPayeBand[];
      };
      gratuity: {
        min_headcount: number;
        min_years: number;
        payment_due_days: number;
      };
    }
  | { supported: false; country: string };

/** How the real statutory-policy fetch can fail, classified by {@link isPayrollAddonRequiredError} — mirrors `use_pay_runs.ts`'s `PayRunsLoadFailure`. */
export type PayrollPoliciesLoadFailure = 'entitlement_required' | 'error';

/** Input the real fetch needs to identify the entity — same shape every `payroll.*` query takes. */
export interface StatutoryPolicyFetchInput {
  firm_id: string;
  entity_id: string;
  /** Optional calendar date (YYYY-MM-DD) to resolve the APIT table for; the API defaults to today. */
  as_of_date?: string;
}

/**
 * The one `payroll.getStatutoryPolicy` call {@link fetchStatutoryPolicy}
 * needs, isolated behind an injectable seam — mirrors `use_employees.ts`'s
 * `EmployeeRecordsFetchDeps` / `defaultEmployeeRecordsFetchDeps()` DI pattern,
 * so the catch-block classification below is unit-testable against a fake
 * client that throws a real-shaped tRPC error.
 */
export interface StatutoryPolicyFetchDeps {
  getStatutoryPolicy: (
    input: StatutoryPolicyFetchInput,
  ) => Promise<StatutoryPolicyRecord>;
}

function defaultStatutoryPolicyFetchDeps(): StatutoryPolicyFetchDeps {
  const client = getTrpcClient();
  return {
    getStatutoryPolicy: (input) =>
      client.payroll.getStatutoryPolicy.query(input),
  };
}

export type StatutoryPolicyFetchResult =
  | { outcome: 'success'; policy: StatutoryPolicyRecord }
  | { outcome: 'failure'; failure: PayrollPoliciesLoadFailure };

/** Classifies a caught `payroll.getStatutoryPolicy` error into the two {@link PayrollPoliciesLoadFailure} buckets. */
function classifyFailure(err: unknown): PayrollPoliciesLoadFailure {
  return isPayrollAddonRequiredError(err) ? 'entitlement_required' : 'error';
}

/**
 * Resolves the entity's real statutory policy, or classifies why it
 * couldn't. Exported (and DI'd via `deps`) so the classification is
 * unit-testable without rendering React — mirrors `use_employees.ts`'s
 * `fetchEmployeeRecords`. `deps` defaults to `undefined` and is resolved
 * INSIDE the `try` below (never as the parameter's own default-value
 * expression, which runs before the function body) — the same lesson
 * `fetchEmployeeRecords`'s own docblock documents: a synchronous
 * `getTrpcClient()` throw there would otherwise escape this function's own
 * `try` entirely.
 */
export async function fetchStatutoryPolicy(
  input: StatutoryPolicyFetchInput,
  deps?: StatutoryPolicyFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<StatutoryPolicyFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultStatutoryPolicyFetchDeps();
    const policy = await resolvedDeps.getStatutoryPolicy(input);
    return { outcome: 'success', policy };
  } catch (err) {
    if (!isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve the real statutory policy:',
        err,
      );
    }
    return { outcome: 'failure', failure: classifyFailure(err) };
  }
}

export type PayrollPoliciesState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; policy: StatutoryPolicyRecord };

/**
 * Pure decision function, exported separately from the hook below so it is
 * unit-testable without rendering React — mirrors `resolveEmployeesState`'s
 * precedence: `no_entity` always wins (an entity-context change must not
 * show a stale `error`/`entitlement_required` from before it changed), then
 * a resolved `policy` wins over a stale `failure` flag (a successful retry
 * must display even if a `failure` from an earlier attempt was never
 * explicitly cleared), then `entitlement_required`, then `error`, then
 * `loading`. In the real hook, `policy`/`failure` are reset together at the
 * start of every fetch, so they are never actually both set at once — this
 * ordering only matters for a synthetic combination a test might construct
 * directly.
 */
export function resolvePayrollPoliciesState(input: {
  entityContext: OAuthEntityContext;
  policy: StatutoryPolicyRecord | undefined;
  failure: PayrollPoliciesLoadFailure | undefined;
}): PayrollPoliciesState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.policy !== undefined) {
    return { status: 'ready', policy: input.policy };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

export interface UsePayrollPoliciesResult {
  state: PayrollPoliciesState;
  /** Re-runs the fetch from scratch — wired to the `error` state's "try again" action. */
  retry: () => void;
}

export interface UsePayrollPoliciesOptions {
  /** Calendar date (YYYY-MM-DD) to resolve the APIT table for, e.g. a pay run's `period_end`. Omit for today. */
  as_of_date?: string;
}

/** The entity's real statutory payroll policy — EPF/ETF rates, the APIT/PAYE bands in force on `as_of_date` (default today), and gratuity thresholds. Read-only; there is no write path (see module docblock). */
export function usePayrollPolicies(
  options: UsePayrollPoliciesOptions = {},
): UsePayrollPoliciesResult {
  const asOfDate = options.as_of_date;
  const entityContext = useOAuthEntityContext();
  const [policy, setPolicy] = React.useState<StatutoryPolicyRecord | undefined>(
    undefined,
  );
  const [failure, setFailure] = React.useState<
    PayrollPoliciesLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    // Captured as its own consts so the nested async closure below
    // references definitely-string values rather than relying on narrowing
    // of `entityContext` surviving across the closure boundary — same
    // pattern `use_employees.ts`/`use_my_pay.ts` use.
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    setPolicy(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      const result = await fetchStatutoryPolicy(
        {
          firm_id: firmId,
          entity_id: entityId,
          ...(asOfDate === undefined ? {} : { as_of_date: asOfDate }),
        },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') {
        setPolicy(result.policy);
      } else {
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken, asOfDate]);

  const state = React.useMemo(
    () => resolvePayrollPoliciesState({ entityContext, policy, failure }),
    [entityContext, policy, failure],
  );

  return {
    state,
    retry: () => {
      setReloadToken((token) => token + 1);
    },
  };
}
